import { useState, type FormEvent } from "react";
import type { DefinitionDetail, DefinitionUsage, EstimateUnit } from "@workcore/schemas";
import { Panel, RowMenu } from "../../ui";
import { InlineText, WriteError, plural, toKey, useDefinitionWrite } from "./shared";

const UNITS: EstimateUnit[] = ["none", "hours", "days", "points", "tshirt"];

function Types({ slug, def, usage, canEdit }: { slug: string; def: DefinitionDetail; usage: DefinitionUsage | undefined; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [unit, setUnit] = useState<EstimateUnit>("none");
  const count = (typeId: number) => usage?.types.find((t) => t.typeId === typeId);
  const add = (e: FormEvent) => {
    e.preventDefault();
    write.mutate(
      { method: "POST", path: `/v1/ws/${slug}/definitions/${def.id}/types`, body: { key: key || toKey(name), name, estimateUnit: unit } },
      { onSuccess: () => (setName(""), setKey("")) },
    );
  };
  return (
    <Panel title="Object types">
      <table className="grid">
        <thead><tr><th>Name</th><th>Plural</th><th>Key</th><th>Estimate</th><th>Objects</th><th /></tr></thead>
        <tbody>
          {def.types.map((t) => {
            const c = count(t.id);
            return (
              <tr key={t.id}>
                <td><InlineText label={`${t.name} name`} value={t.name} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/types/${t.id}`, body: { name: v } })} /></td>
                <td><InlineText label={`${t.name} plural`} value={t.plural} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/types/${t.id}`, body: { plural: v } })} /></td>
                <td><code>{t.key}</code></td>
                <td>
                  <select aria-label={`${t.name} estimate unit`} disabled={!canEdit} value={t.estimateUnit} onChange={(e) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/types/${t.id}`, body: { estimateUnit: e.target.value } })}>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td>{c ? c.objects : "…"}{c && c.includingDeleted > c.objects && <span className="muted small"> (+{c.includingDeleted - c.objects} deleted)</span>}</td>
                <td className="row-actions">
                  {canEdit && <RowMenu label={`Actions for ${t.name}`} items={[{ label: "Remove…", danger: true, onSelect: () => write.mutate({ method: "DELETE", path: `/v1/ws/${slug}/types/${t.id}`, impact: `Remove type '${t.name}' with its states and transitions.` }) }]} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && (
        <form autoComplete="off" className="inline wrap" onSubmit={add}>
          <label className="inline-field"><span>Name</span><input aria-label="New type name" placeholder="e.g. Risk" required value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="inline-field"><span>Key</span><input aria-label="New type key" placeholder={`key (${toKey(name) || "auto"})`} value={key} onChange={(e) => setKey(toKey(e.target.value))} /></label>
          <label className="inline-field"><span>Estimates</span><select aria-label="New type estimate unit" value={unit} onChange={(e) => setUnit(e.target.value as EstimateUnit)}>
            {UNITS.map((u) => <option key={u} value={u}>estimates in {u}</option>)}
          </select></label>
          <button type="submit">Add type</button>
        </form>
      )}
      <p className="muted small">Keys never change. A type with objects (including deleted ones) cannot be removed; the dry run says so before anything happens.</p>
      <WriteError error={write.error} />
    </Panel>
  );
}

function Tree({ def, parent, depth, seen }: { def: DefinitionDetail; parent: number | null; depth: number; seen: Set<number> }) {
  const children = def.containment.filter((r) => r.parentTypeId === parent).map((r) => def.types.find((t) => t.id === r.childTypeId)!).filter(Boolean);
  if (children.length === 0) return null;
  return (
    <ul className="tree">
      {children.map((t) => (
        <li key={`${parent}-${t.id}`}>
          <span className="node">{t.name}</span>
          {seen.has(t.id) || depth > 8 ? <span className="muted small"> ↻ (contains itself further up)</span> : <Tree def={def} parent={t.id} depth={depth + 1} seen={new Set([...seen, t.id])} />}
        </li>
      ))}
    </ul>
  );
}

function Containment({ slug, def, canEdit }: { slug: string; def: DefinitionDetail; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const [parent, setParent] = useState("root");
  const [child, setChild] = useState("");
  const name = (id: number | null) => (id === null ? "Project root" : def.types.find((t) => t.id === id)?.name ?? `#${id}`);
  const path = `/v1/ws/${slug}/definitions/${def.id}/containment`;
  return (
    <Panel title="What can contain what">
      <div className="tree-wrap">
        <span className="node root">Project root</span>
        <Tree def={def} parent={null} depth={0} seen={new Set()} />
      </div>
      <table className="grid">
        <thead><tr><th>Parent</th><th /><th>Child</th><th /></tr></thead>
        <tbody>
          {def.containment.map((r) => (
            <tr key={`${r.parentTypeId}-${r.childTypeId}`}>
              <td>{name(r.parentTypeId)}</td>
              <td className="muted">may contain</td>
              <td>{name(r.childTypeId)}</td>
              <td className="row-actions">
                {canEdit && <RowMenu label={`Actions for ${name(r.parentTypeId)} containing ${name(r.childTypeId)}`} items={[{ label: "Remove…", danger: true, onSelect: () => write.mutate({ method: "DELETE", path, body: r, impact: `Stop ${name(r.parentTypeId)} containing ${name(r.childTypeId)}. New objects can no longer be placed there.` }) }]} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <form autoComplete="off"
          className="inline wrap"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            write.mutate({ method: "POST", path, body: { parentTypeId: parent === "root" ? null : Number(parent), childTypeId: Number(child) } }, { onSuccess: () => setChild("") });
          }}
        >
          <label className="inline-field"><span>Parent</span><select aria-label="Parent type" value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="root">Project root</option>
            {def.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></label>
          <label className="inline-field"><span>May contain</span><select aria-label="Child type" required value={child} onChange={(e) => setChild(e.target.value)}>
            <option value="">choose a type…</option>
            {def.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></label>
          <button type="submit">Add rule</button>
        </form>
      )}
      <p className="muted small">A rule that existing objects rely on cannot be removed; the dry run lists them.</p>
      <WriteError error={write.error} />
    </Panel>
  );
}

export function StructureTab(props: { slug: string; def: DefinitionDetail; usage: DefinitionUsage | undefined; canEdit: boolean }) {
  return (
    <>
      <Types {...props} />
      <Containment slug={props.slug} def={props.def} canEdit={props.canEdit} />
    </>
  );
}

export { plural };
